/**
 * SVG placeholder generator for product imagery.
 *
 * Why SVG data URIs instead of raster placeholders:
 *  - Zero network requests (inlined into the HTML / <img src>).
 *  - Crisp at any DPR, no Cumulative Layout Shift when paired with width/height.
 *  - Easy to swap to real photography later — same <img> tag, just change the src.
 *  - Search engines index the surrounding alt + caption text, not the raster content,
 *    so a clean SVG with the SKU is enough until real shots are available.
 */

const BRAND_PALETTES = {
  liugong: { bg: '#FFB300', fg: '#1a1a1a', accent: '#ff6b35' },
  sdlg: { bg: '#FF8C42', fg: '#1a1a1a', accent: '#ff6b35' },
  xcmg: { bg: '#FFA500', fg: '#1a1a1a', accent: '#ff6b35' },
  zf: { bg: '#2d2d2d', fg: '#ffffff', accent: '#ff6b35' },
  default: { bg: '#2d2d2d', fg: '#ffffff', accent: '#ff6b35' },
};

function escapeXml(str = '') {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function paletteFor(brandKey = '') {
  const key = brandKey.toLowerCase();
  if (key.includes('liugong')) return BRAND_PALETTES.liugong;
  if (key.includes('sdlg')) return BRAND_PALETTES.sdlg;
  if (key.includes('xcmg')) return BRAND_PALETTES.xcmg;
  if (key.includes('zf')) return BRAND_PALETTES.zf;
  return BRAND_PALETTES.default;
}

/**
 * Build a deterministic SVG placeholder marked up with the part number, the
 * human product name, and a brand label. Returned as a `data:image/svg+xml`
 * URI ready to drop into <img src="…">.
 */
export function buildProductPlaceholder({
  partNumber = '',
  productName = '',
  brandLabel = '',
  width = 800,
  height = 600,
} = {}) {
  const palette = paletteFor(brandLabel);
  const safePart = escapeXml(partNumber);
  const safeName = escapeXml(productName);
  const safeBrand = escapeXml(brandLabel);

  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" role="img" aria-label="${safeName} ${safePart} placeholder image">
  <defs>
    <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="${palette.bg}" stop-opacity="1"/>
      <stop offset="1" stop-color="${palette.bg}" stop-opacity="0.78"/>
    </linearGradient>
    <pattern id="p" width="24" height="24" patternUnits="userSpaceOnUse">
      <path d="M0 24 L24 0" stroke="${palette.fg}" stroke-opacity="0.06" stroke-width="1"/>
    </pattern>
  </defs>
  <rect width="100%" height="100%" fill="url(#g)"/>
  <rect width="100%" height="100%" fill="url(#p)"/>
  <rect x="40" y="40" width="${width - 80}" height="${height - 80}" rx="20" ry="20" fill="none" stroke="${palette.fg}" stroke-opacity="0.18" stroke-width="2"/>
  <g font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" text-anchor="middle">
    <text x="${width / 2}" y="${height / 2 - 60}" fill="${palette.fg}" fill-opacity="0.72" font-size="22" font-weight="600" letter-spacing="4">
      ${safeBrand.toUpperCase()}
    </text>
    <text x="${width / 2}" y="${height / 2}" fill="${palette.fg}" font-size="44" font-weight="800">
      ${safeName}
    </text>
    <text x="${width / 2}" y="${height / 2 + 60}" fill="${palette.accent}" font-size="34" font-weight="700" font-family="ui-monospace, SFMono-Regular, Menlo, monospace">
      ${safePart}
    </text>
    <text x="${width / 2}" y="${height - 70}" fill="${palette.fg}" fill-opacity="0.55" font-size="16" font-weight="500" letter-spacing="2">
      TRADE ZONE EQUIPMENTS
    </text>
  </g>
</svg>`;

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg.trim())}`;
}

/**
 * Convenience helper — returns the SEO-friendly alt text for an item image.
 * Keep this in sync with what Google's image search expects: descriptive,
 * keyword-rich, but not stuffed.
 */
export function buildProductImageAlt({ productName, partNumber, brandLabel }) {
  const parts = [
    productName,
    partNumber ? `part number ${partNumber}` : null,
    brandLabel ? `for ${brandLabel}` : null,
    'spare parts supplier — Trade Zone Equipments',
  ].filter(Boolean);
  return parts.join(' ');
}
